/**
 * Lesson lifecycle vocabulary — frontend mirror
 * (Cancellation Truth Migration · Batch C1, Feb 2026).
 *
 * Mirrors `backend/domain/lesson_lifecycle.py`. Every value and
 * every helper here MUST match the backend semantics byte-for-byte.
 *
 * Batch C1 is entirely additive — no frontend surface has been
 * migrated to import from this module yet. Reader migrations land
 * in Batch C2.
 *
 * Terminology
 * -----------
 *  • Active diary state         — appointment occupies the working diary.
 *  • Removed from active diary  — must not count as an active commitment.
 *  • Final inactive state       — concluded, will not resume.
 *  • Reversible inactive state  — temporarily removed, may restore.
 *  • Genuine cancellation       — actual cancellation; NOT a synonym
 *                                 for every inactive state.
 *
 * Do NOT rename any of the sets that include `paused` or `suspended`
 * as "terminal". Those two are reversible and are not terminal.
 */

// Broad category — the row is not on the active diary.
export const REMOVED_FROM_ACTIVE_DIARY = Object.freeze(new Set([
  "cancelled",
  "declined",
  "expired",
  "paused",
  "suspended",
  "no_show",
]));

// Narrow — only actual cancellations. KPI surfaces must use this.
export const GENUINELY_CANCELLED = Object.freeze(new Set(["cancelled"]));

// Inactive rows that may return to the diary.
export const REVERSIBLE_INACTIVE_STATES = Object.freeze(new Set([
  "paused",
  "suspended",
]));

// Inactive rows that will not resume.
export const FINAL_INACTIVE_STATES = Object.freeze(new Set([
  "cancelled",
  "declined",
  "expired",
  "no_show",
]));


/**
 * Return the canonical lifecycle string for a lesson row.
 *
 * Prefers `lifecycle_status` and falls back to legacy `status`,
 * matching `backend/domain/lesson_lifecycle.py::lesson_status`.
 * Returns "" when neither field is populated.
 */
export function lessonStatus(event) {
  if (!event || typeof event !== "object") return "";
  const canonical = event.lifecycle_status;
  if (canonical) return String(canonical);
  const legacy = event.status;
  return legacy ? String(legacy) : "";
}


/** True if the row is still on the active diary. */
export function isActiveLesson(event) {
  return !REMOVED_FROM_ACTIVE_DIARY.has(lessonStatus(event));
}


/** True if the row has been removed from the active diary. */
export function isRemovedFromActiveDiary(event) {
  return REMOVED_FROM_ACTIVE_DIARY.has(lessonStatus(event));
}


/**
 * Return the canonical stored state, or null when it is
 * absent/unknown. Not named `terminalCategory` — `paused` and
 * `suspended` are reversible, not terminal.
 */
export function lifecycleCategory(event) {
  const s = lessonStatus(event);
  return s ? s : null;
}
